import React, { useCallback } from 'react';
import { useDragToScroll } from '@/hooks';
import TablePagination from '@mui/material/TablePagination';
import { CustomTableHead } from '../../shared/EquipmentTable/CustomTableHead';
import { CustomTableBody } from '../../shared/EquipmentTable/CustomTableBody';
import {
  TablePaginationContainer,
  StyledTableContainer,
  ScrollableBox,
  StyledTable,
  PaginationBox,
} from '../../Insights/EquipmentTable/styles';
import { Columns } from '@/types';
import { RowData } from '../../Insights/InsightsExplorer/PointOfInterests/usePoiData';

type CustomTableProps = {
  columns: Columns[];
  data: RowData[];
  totalCount: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  handleSort: (column: string, sortType?: 'NUMBER' | 'STRING') => void;
  onRowSelect: (selectedIds: string[]) => void;
  selectedRowIds: string[];
  onRowClick: (event: RowClickEvent, row: RowData) => void;
  showGearIcon?: boolean;
  selectedRow?: string;
  sortBy: string;
  sortDirection: number;
  name?: string;
};

type RowClickEvent = React.MouseEvent<HTMLTableRowElement, MouseEvent>;

export const CustomTable: React.FC<CustomTableProps> = ({
  columns,
  data,
  totalCount,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  handleSort,
  sortBy,
  sortDirection,
  onRowSelect,
  selectedRowIds,
  onRowClick,
  showGearIcon = true,
  selectedRow,
  name,
}) => {
  const handlePageChange = (_: unknown, newPage: number) => {
    onPageChange(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onRowsPerPageChange(+event.target.value);
  };

  const handleHeaderCheckboxChange = useCallback(() => {
    if (selectedRowIds.length === data.length) {
      onRowSelect([]);
    } else {
      onRowSelect(data.map((item) => item.id));
    }
  }, [data, onRowSelect, selectedRowIds.length]);

  const { containerReference, handleMouseDown } = useDragToScroll();

  return (
    <StyledTableContainer data-testid={`${name ?? 'custom'}-table`}>

      <ScrollableBox ref={containerReference} onMouseDown={handleMouseDown}>
        <StyledTable stickyHeader>
          <CustomTableHead
            activeColumns={columns}
            toggleDialog={() => {}}
            handleHeaderCheckboxChange={handleHeaderCheckboxChange}
            handleSort={handleSort}
            orderBy={sortBy}
            order={sortDirection}
            isChecked={selectedRowIds.length === data.length}
            showGearIcon={showGearIcon}
            selectedRowIds={selectedRowIds}
            rows={data}
          />
          <CustomTableBody
            data={data}
            activeColumns={columns}
            onCheckboxChange={(id) => {
              const newSelectedIds = selectedRowIds.includes(id)
                ? selectedRowIds.filter((selectedId) => selectedId !== id)
                : [...selectedRowIds, id];
              onRowSelect(newSelectedIds);
            }}
            selectedIds={selectedRowIds}
            onRowClick={onRowClick}
            selectedRow={selectedRow}
          />
        </StyledTable>
      </ScrollableBox>
      <PaginationBox>
        <TablePaginationContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[
              { label: '10', value: 10 },
              { label: '20', value: 20 },
              { label: '50', value: 50 },
              { label: '100', value: 100 },
              { label: 'All', value: totalCount },
            ]}
            data-testid={`${name ?? 'custom'}-table-pagination`}
          />
        </TablePaginationContainer>
      </PaginationBox>
    </StyledTableContainer>
  );
};
